import React, { FC, useEffect, useState } from 'react';

import { GrowthBookProvider as SDKGrowthBookProvider } from '@growthbook/growthbook-react';

import Api from '~/Api';
import FullscreenPreloader from '~/components/atoms/FullscreenPreloader';
import { USER_ID_KEY } from '~/constants';
import projectConfig from '~/projects/config';
import { growthBook } from '~/services/abTests/growthBook';
import localStorage from '~/services/storage/localStorage';
import { useAppSelector } from '~/store/hooks/useAppSelector';

import { funnelAttributesService } from '../funnelAttributes';

const growthBookInstance = growthBook.getInstance();

type Props = {
  children: React.ReactNode;
};

const normalizeCountry = (country?: string | null): string | null => {
  const normalizedCountry = country?.trim().toUpperCase();

  return normalizedCountry || null;
};

const GrowthBookProvider: FC<Props> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);

  const { id, created_at, is_stripe_user, country } = useAppSelector(
    (state) => state.profile,
  );

  useEffect(() => {
    const loadGrowthBook = async (): Promise<void> => {
      try {
        funnelAttributesService.captureFromUrl();

        const normalizedProfileCountry = normalizeCountry(country);
        const resolvedCountry =
          normalizedProfileCountry || normalizeCountry(await Api.getCountry());

        await growthBook.init({
          id: id || localStorage.getByKey(USER_ID_KEY) || undefined,
          project: projectConfig.project,
          created_at,
          is_stripe_user,
          country: resolvedCountry,
          ...funnelAttributesService.getAll(),
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadGrowthBook();
  }, [id, created_at, is_stripe_user, country]);

  return (
    <SDKGrowthBookProvider growthbook={growthBookInstance}>
      {isLoading ? <FullscreenPreloader /> : <>{children}</>}
    </SDKGrowthBookProvider>
  );
};

export default GrowthBookProvider;
